import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, Stack, Typography } from '@mui/material';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import theme from '../../theme';
import useV1AccessMode from '../../hooks/useV1AccessMode';
import {
  V1_ACCESS_MODES,
  V1_MIGRATION_NOTICE_DISMISSED_KEY as DISMISSED_KEY,
  V1_MIGRATION_NOTICE_DISMISS_TTL_MS as DISMISS_TTL_MS,
  V1_MIGRATION_COUNTDOWN_SECONDS as COUNTDOWN_SECONDS,
} from '../../lib/constants';

const RING_SIZE = 68;

const { black, primary, grey } = theme.palette;

const ASSURANCES = [
  { icon: LockOutlinedIcon, text: 'Sign in with the same email and password.' },
  { icon: CloudDoneOutlinedIcon, text: 'Your orders, missions and files are already there.' },
  { icon: BoltOutlinedIcon, text: 'Rebuilt to be faster and simpler to work in.' },
];

const styles = {
  paper: { borderRadius: '16px', overflow: 'hidden', maxWidth: '520px' },
  header: {
    background: black.darkSeaBlack,
    padding: { xs: '28px 24px', sm: '32px 36px' },
    position: 'relative',
  },
  headerGlow: {
    position: 'absolute',
    top: '-70px',
    right: '-40px',
    width: '190px',
    height: '190px',
    borderRadius: '50%',
    background: primary.main,
    opacity: 0.16,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: primary.main,
    color: black.darkSeaBlack,
    borderRadius: '999px',
    padding: '5px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  headline: { color: grey.white, marginTop: '14px', fontSize: { xs: '24px', sm: '28px' }, lineHeight: 1.15 },
  headerCopy: { color: black.stoneSilver, marginTop: '8px', fontSize: '14px', lineHeight: 1.5 },
  body: { padding: { xs: '24px', sm: '28px 36px' } },
  assuranceIcon: { fontSize: '18px', color: black.coolGrey, marginTop: '1px' },
  assuranceText: { fontSize: '14px', color: black.paragraphText, lineHeight: 1.45 },
  countdownRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    background: primary.extraPalePistachio,
    border: `1px solid ${primary.palePistachio}`,
    borderRadius: '12px',
    padding: '14px 16px',
    marginTop: '22px',
  },
  ringWrap: { position: 'relative', display: 'inline-flex', flexShrink: 0 },
  ringTrack: { color: primary.palePistachio, position: 'absolute', left: 0 },
  ringValue: { color: black.darkSeaBlack },
  ringLabel: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 600,
    color: black.darkSeaBlack,
  },
  countdownTitle: { fontSize: '14px', fontWeight: 600, color: black.darkSeaBlack },
  countdownHint: { fontSize: '13px', color: black.coolGrey, marginTop: '2px' },
  footer: {
    display: 'flex',
    flexDirection: { xs: 'column-reverse', sm: 'row' },
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
    padding: { xs: '0 24px 24px', sm: '0 36px 28px' },
  },
  primaryButton: {
    background: primary.main,
    color: black.darkSeaBlack,
    borderRadius: '999px',
    textTransform: 'none',
    fontSize: '15px',
    fontWeight: 600,
    padding: '11px 26px',
    width: { xs: '100%', sm: 'auto' },
    boxShadow: 'none',
    '&:hover': { background: primary.pistachio, boxShadow: 'none' },
  },
  ghostButton: {
    color: black.coolGrey,
    borderRadius: '999px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    padding: '11px 20px',
    width: { xs: '100%', sm: 'auto' },
    '&:hover': { background: grey.lightGrey },
  },
};

const wasDismissedRecently = () => {
  try {
    const at = Number(window.localStorage.getItem(DISMISSED_KEY));
    return Boolean(at) && Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    // A browser that refuses storage just gets the notice again next visit.
  }
};

const goToV2 = () => {
  const appUrl = process.env.GATSBY_V2_APP_URL;
  if (appUrl) window.location.assign(/^https?:\/\//.test(appUrl) ? appUrl : `https://${appUrl}`);
};

/**
 * Tells visitors the platform has moved. Mounted at the root so it also covers
 * the marketing and sign-in pages, which sit outside the logged-in app tree.
 */
export default function V1MigrationNotice() {
  const mode = useV1AccessMode();
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    setMounted(true);
    setDismissed(wasDismissedRecently());
  }, []);

  const enforced = mode === V1_ACCESS_MODES.enforced;

  useEffect(() => {
    if (!mounted || !enforced || redirecting) return undefined;
    const tick = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(tick);
  }, [mounted, enforced, redirecting]);

  // Latching on `redirecting` stops the tick and swaps the ring for a spinner,
  // so a slow navigation never shows a stuck zero or counts into negatives.
  useEffect(() => {
    if (!enforced || redirecting || secondsLeft > 0) return;
    setRedirecting(true);
    goToV2();
  }, [enforced, redirecting, secondsLeft]);

  if (!mounted || mode === V1_ACCESS_MODES.off) return null;
  if (!enforced && dismissed) return null;

  const dismiss = () => {
    rememberDismissal();
    setDismissed(true);
  };

  const leave = () => {
    setRedirecting(true);
    goToV2();
  };

  const progress = redirecting ? 100 : ((COUNTDOWN_SECONDS - secondsLeft) / COUNTDOWN_SECONDS) * 100;

  return (
    <Dialog open fullWidth maxWidth="sm" PaperProps={{ sx: styles.paper }} onClose={enforced ? undefined : dismiss}>
      <Box sx={styles.header}>
        <Box sx={styles.headerGlow} />
        <Box sx={{ position: 'relative' }}>
          <Box component="span" sx={styles.badge}>
            <RocketLaunchOutlinedIcon sx={{ fontSize: '14px' }} />
            New Globhe platform
          </Box>
          <Typography variant="h4" sx={styles.headline}>
            We&apos;ve moved to a new platform
          </Typography>
          <Typography sx={styles.headerCopy}>
            Globhe has a new home. Everything you work with here is waiting for you there.
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.body}>
        <Stack spacing={1.5}>
          {ASSURANCES.map(({ icon: Icon, text }) => (
            <Stack key={text} direction="row" spacing={1.25} alignItems="flex-start">
              <Icon sx={styles.assuranceIcon} />
              <Typography sx={styles.assuranceText}>{text}</Typography>
            </Stack>
          ))}
        </Stack>

        {enforced && (
          <Box sx={styles.countdownRow}>
            <Box sx={styles.ringWrap}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={RING_SIZE}
                thickness={3.5}
                sx={styles.ringTrack}
              />
              <CircularProgress
                variant={redirecting ? 'indeterminate' : 'determinate'}
                value={progress}
                size={RING_SIZE}
                thickness={3.5}
                sx={styles.ringValue}
              />
              {!redirecting && <Box sx={styles.ringLabel}>{secondsLeft}</Box>}
            </Box>
            <Box>
              <Typography sx={styles.countdownTitle}>
                {redirecting ? 'Taking you there now…' : 'Redirecting you automatically'}
              </Typography>
              <Typography sx={styles.countdownHint}>
                {redirecting
                  ? 'This only takes a moment.'
                  : `In ${secondsLeft} ${secondsLeft === 1 ? 'second' : 'seconds'} — or go now.`}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={styles.footer}>
        {!enforced && (
          <Button sx={styles.ghostButton} onClick={dismiss}>
            Continue here for now
          </Button>
        )}
        <Button sx={styles.primaryButton} onClick={leave} disabled={redirecting} endIcon={<ArrowForwardRoundedIcon />}>
          Go to the new platform
        </Button>
      </Box>
    </Dialog>
  );
}
